import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { 
  CheckCircle, 
  Clock, 
  DollarSign, 
  Droplets, 
  Building2, 
  Hotel, 
  TreePalm, 
  Users, 
  Home,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  ArrowRight,
  Zap,
  Shield,
  Award
} from 'lucide-react';

const CommercialPoolRobotPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "Reduces Labour Cost by 70%",
      description: "Eliminate the need for manual pool cleaners. One robotic pool cleaner replaces multiple staff members, delivering consistent results without human error or fatigue."
    },
    {
      icon: Droplets,
      title: "Cleans Floor, Walls & Waterline",
      description: "Advanced navigation technology ensures complete coverage of your pool surface, including hard-to-reach corners, walls, and the waterline where algae typically forms."
    },
    {
      icon: Clock,
      title: "Saves Time & Chemicals",
      description: "Automated cleaning cycles reduce chemical dependency by up to 30% while completing cleaning tasks in a fraction of the time required for manual cleaning."
    },
    {
      icon: Building2,
      title: "Ideal for Commercial Properties",
      description: "Purpose-built for large pools at hotels, resorts, clubs, and apartment complexes. Handles pools up to 25 meters with ease and reliability."
    }
  ];

  const industries = [
    { icon: Hotel, name: "Hotels", description: "5-star hotels, business hotels, boutique properties" },
    { icon: TreePalm, name: "Resorts", description: "Beach resorts, hill stations, wellness retreats" },
    { icon: Users, name: "Clubs", description: "Country clubs, sports clubs, golf clubs" },
    { icon: Home, name: "Luxury Villas", description: "Private estates, farmhouses, vacation homes" },
    { icon: Building2, name: "Apartments", description: "Gated communities, high-rise complexes, townships" }
  ];

  const comparisonData = [
    { feature: "Labour Cost", manual: "₹15,000–25,000/month", robotic: "One-time investment" },
    { feature: "Cleaning Time", manual: "4–6 hours daily", robotic: "2–3 hours (automated)" },
    { feature: "Consistency", manual: "Variable quality", robotic: "Uniform results every time" },
    { feature: "Chemical Usage", manual: "High consumption", robotic: "30% reduction" },
    { feature: "Coverage", manual: "Often incomplete", robotic: "100% floor, walls & waterline" },
    { feature: "Supervision Required", manual: "Constant monitoring", robotic: "Set and forget" },
    { feature: "Night Cleaning", manual: "Not possible", robotic: "Programmable schedules" },
    { feature: "ROI Period", manual: "Ongoing expense", robotic: "6–12 months payback" }
  ];

  const faqs = [
    {
      question: "What is the best commercial pool cleaning robot for hotels in India?",
      answer: "The Harpalyk Commercial Pool Robot is specifically designed for Indian hospitality requirements. It handles pools up to 25 meters, operates on Indian voltage standards, and comes with local service support across 50+ cities. Our robots are trusted by over 100 luxury hotels and resorts across India."
    },
    {
      question: "How much does a robotic pool cleaner cost for commercial use?",
      answer: "Commercial robotic pool cleaners in India range from ₹2,50,000 to ₹8,00,000 depending on pool size and features. Harpalyk offers flexible financing options including EMI plans starting at ₹15,000/month. The typical ROI period is 6–12 months compared to manual cleaning costs."
    },
    {
      question: "Can robotic pool cleaners work in large Olympic-size pools?",
      answer: "Yes, Harpalyk commercial pool robots are designed for large pools up to 25 meters in length and 50 meters in width. For Olympic-size pools, we recommend our flagship model with extended cable length and enhanced suction power for maximum coverage."
    },
    {
      question: "How does a commercial pool robot reduce maintenance costs?",
      answer: "Commercial pool robots reduce costs in multiple ways: 70% reduction in labour costs, 30% savings on pool chemicals, lower electricity consumption compared to traditional pumps, and reduced wear on filtration systems. Most properties achieve complete ROI within 6–12 months."
    },
    {
      question: "Is after-sales service available for pool cleaning robots in India?",
      answer: "Harpalyk provides comprehensive after-sales support with 50+ authorized service centers across India. We offer 2-year warranty, annual maintenance contracts, 24/7 customer support, and doorstep repair services in all major cities including Delhi, Mumbai, Bangalore, Chennai, and Goa."
    }
  ];

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Product Schema
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Harpalyk Commercial Pool Cleaning Robot",
    "description": "Advanced robotic pool cleaner for hotels, resorts, and commercial swimming pools in India. Cleans floor, walls, and waterline automatically.",
    "brand": {
      "@type": "Brand",
      "name": "Harpalyk"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Harpalyk Engineering Private Limited"
    },
    "category": "Commercial Pool Cleaning Equipment",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "INR",
      "price": "250000",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    }
  };

  // Local Business Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Harpalyk Engineering Private Limited",
    "description": "Leading manufacturer of commercial robotic pool cleaners for hotels and resorts in India",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Gurugram",
      "addressRegion": "Haryana",
      "addressCountry": "India"
    },
    "telephone": "+91-7011370322",
    "email": "sales@harpalyk.com",
    "url": "https://harpalyk.com",
    "priceRange": "₹₹₹"
  };

  const scrollToContact = () => {
    document.getElementById('pool-contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Commercial Pool Cleaning Robot India | Harpalyk Engineering</title>
        <meta name="description" content="India's #1 commercial robotic pool cleaner for hotels & resorts. Reduce labour costs by 70%. Automatic cleaning for floor, walls & waterline. Request demo today." />
        <meta name="keywords" content="commercial pool cleaning robot india, robotic pool cleaner for hotels, swimming pool cleaning robot, automatic pool cleaning robot, hotel pool maintenance automation, commercial robotic pool cleaner, large pool cleaning robot" />
        <link rel="canonical" href="https://harpalyk.com/commercial-pool-cleaning-robot-india" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Commercial Pool Cleaning Robot India | Harpalyk Engineering" />
        <meta property="og:description" content="India's #1 commercial robotic pool cleaner for hotels & resorts. Reduce labour costs by 70%. Request demo today." />
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://harpalyk.com/commercial-pool-cleaning-robot-india" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <Breadcrumbs items={[{ label: 'Commercial Pool Cleaning Robot' }]} />
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 text-white py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  #1 Commercial Pool Robot in India
                </span>
                <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                  Commercial Pool Cleaning Robot for Hotels & Resorts in India
                </h1>
                <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                  Transform your pool maintenance with India's most advanced <strong>commercial robotic pool cleaner</strong>. 
                  Designed specifically for luxury hotels, resorts, and large swimming pools. Reduce labour costs by 70% 
                  while achieving spotless results every single day.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button 
                    onClick={scrollToContact}
                    className="bg-cyan-500 hover:bg-cyan-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all"
                  >
                    Request Free Demo <ArrowRight className="w-5 h-5" />
                  </button>
                  <a 
                    href="tel:+917011370322"
                    className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all"
                  >
                    <Phone className="w-5 h-5" /> Call Now
                  </a>
                </div>
                
                {/* Trust Indicators */}
                <div className="flex flex-wrap gap-6 mt-10 pt-8 border-t border-white/20">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">100+</p>
                    <p className="text-sm text-blue-200">Hotels Trust Us</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">70%</p>
                    <p className="text-sm text-blue-200">Cost Reduction</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">50+</p>
                    <p className="text-sm text-blue-200">Service Centers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">4.8/5</p>
                    <p className="text-sm text-blue-200">Customer Rating</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-3xl p-8 backdrop-blur-sm border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=600&q=80" 
                    alt="Commercial Pool Cleaning Robot for Hotels in India" 
                    className="rounded-2xl w-full shadow-2xl"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-white text-blue-900 px-6 py-3 rounded-xl shadow-lg">
                    <p className="font-bold">Starting ₹2,50,000</p>
                    <p className="text-sm text-gray-600">EMI from ₹15,000/mo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Why Manual Pool Cleaning is Costing Your Hotel Lakhs Every Year
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Traditional pool maintenance methods are draining your budget and compromising guest experience
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "High Labour Costs", desc: "₹15,000–25,000 monthly on pool staff salaries alone", icon: "💸" },
                { title: "Inconsistent Results", desc: "Manual cleaning often misses walls, corners, and waterline", icon: "😤" },
                { title: "Time Consuming", desc: "4–6 hours daily tied up in pool maintenance activities", icon: "⏰" },
                { title: "Guest Complaints", desc: "Dirty pools lead to negative reviews and lost bookings", icon: "⭐" }
              ].map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-red-100">
                  <span className="text-4xl mb-4 block">{item.icon}</span>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">The Solution</span>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-6">
                  Robotic Pool Cleaning Technology Built for Indian Hotels
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Harpalyk's <strong>commercial pool cleaning robot</strong> represents the future of hotel pool maintenance in India. 
                  Using advanced AI navigation and powerful suction technology, our <strong>automatic pool cleaning robot</strong> delivers 
                  consistent, thorough cleaning without human intervention.
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Specifically engineered for the Indian hospitality industry, our <strong>swimming pool cleaning robot</strong> handles 
                  the unique challenges of tropical climates—from algae growth to dust accumulation—ensuring your pool remains 
                  pristine and guest-ready 24/7.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Made for Indian Conditions</span>
                  </div>
                  <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Local Service Support</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&q=80" 
                  alt="Hotel swimming pool maintenance robot" 
                  className="rounded-xl shadow-lg"
                />
                <img 
                  src="https://images.unsplash.com/photo-1540541338287-41700207dee6?w=400&q=80" 
                  alt="Resort pool cleaning automation" 
                  className="rounded-xl shadow-lg mt-8"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-6 lg:px-12 bg-gradient-to-b from-white to-cyan-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">Key Benefits</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Why Hotels Choose Harpalyk Commercial Pool Robots
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Join 100+ luxury properties across India that have transformed their pool maintenance operations
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-100 p-4 rounded-xl">
                      <benefit.icon className="w-8 h-8 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Overview */}
        <section className="py-20 px-6 lg:px-12 bg-blue-900 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-cyan-400 font-semibold uppercase tracking-wide text-sm">Product Overview</span>
                <h2 className="text-3xl lg:text-4xl font-bold mt-2 mb-6">
                  Harpalyk Commercial Pool Cleaning Robot
                </h2>
                <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                  India's most trusted <strong>robotic pool cleaner for hotels</strong>. Engineered for commercial-grade 
                  performance with hospitality-specific features.
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    "Cleans pools up to 25m x 50m",
                    "4-stage filtration system",
                    "Smart navigation with wall climbing",
                    "Programmable cleaning schedules",
                    "Energy-efficient operation (150W)",
                    "Remote monitoring via mobile app",
                    "2-year comprehensive warranty",
                    "Pan-India service network"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                      <span className="text-blue-100">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <div className="bg-white/10 backdrop-blur px-6 py-4 rounded-xl">
                    <p className="text-cyan-400 font-bold text-2xl">₹2,50,000</p>
                    <p className="text-blue-200 text-sm">Starting Price</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur px-6 py-4 rounded-xl">
                    <p className="text-cyan-400 font-bold text-2xl">6-12 Months</p>
                    <p className="text-blue-200 text-sm">ROI Period</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur px-6 py-4 rounded-xl">
                    <p className="text-cyan-400 font-bold text-2xl">0% EMI</p>
                    <p className="text-blue-200 text-sm">Available</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?w=600&q=80" 
                  alt="Harpalyk Commercial Pool Cleaning Robot" 
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute top-4 left-4 bg-cyan-500 text-white px-4 py-2 rounded-lg font-bold">
                  Best Seller
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">Comparison</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Manual Cleaning vs Robotic Pool Cleaning
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                See why leading hotels are switching to automated <strong>commercial pool cleaning robots</strong>
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-left font-semibold text-red-300">Manual Cleaning</th>
                    <th className="px-6 py-4 text-left font-semibold text-cyan-300">Harpalyk Robot</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-medium text-gray-900">{row.feature}</td>
                      <td className="px-6 py-4 text-red-600">{row.manual}</td>
                      <td className="px-6 py-4 text-green-600 font-medium">{row.robotic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Industries Served */}
        <section className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">Industries We Serve</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Trusted by India's Leading Hospitality Brands
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our <strong>hotel pool maintenance automation</strong> solutions serve diverse commercial properties
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
              {industries.map((industry, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center group">
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-600 transition-all">
                    <industry.icon className="w-8 h-8 text-cyan-600 group-hover:text-white transition-all" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{industry.name}</h3>
                  <p className="text-gray-500 text-sm">{industry.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="pool-contact" className="py-20 px-6 lg:px-12 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Transform Your Pool Maintenance?
            </h2>
            <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
              Join 100+ hotels and resorts that have already reduced pool maintenance costs by 70%. 
              Request a free demo today and see the Harpalyk commercial pool robot in action.
            </p>
            
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-xl mx-auto">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="Your Name" 
                    className="px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:border-white"
                  />
                  <input 
                    type="text" 
                    placeholder="Hotel/Company Name" 
                    className="px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:border-white"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input 
                    type="tel" 
                    placeholder="Phone Number" 
                    className="px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:border-white"
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address" 
                    className="px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:border-white"
                  />
                </div>
                <select className="px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-white">
                  <option value="" className="text-gray-900">Select Property Type</option>
                  <option value="hotel" className="text-gray-900">Hotel</option>
                  <option value="resort" className="text-gray-900">Resort</option>
                  <option value="club" className="text-gray-900">Club</option>
                  <option value="apartment" className="text-gray-900">Apartment Complex</option>
                  <option value="villa" className="text-gray-900">Villa/Farmhouse</option>
                </select>
                <button className="bg-white text-blue-700 px-8 py-4 rounded-lg font-bold hover:bg-cyan-100 transition-all flex items-center justify-center gap-2">
                  Request Free Demo <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 mt-10">
              <a href="tel:+917011370322" className="flex items-center gap-2 text-cyan-100 hover:text-white">
                <Phone className="w-5 h-5" /> +91-7011370322
              </a>
              <a href="mailto:sales@harpalyk.com" className="flex items-center gap-2 text-cyan-100 hover:text-white">
                <Mail className="w-5 h-5" /> sales@harpalyk.com
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">FAQs</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600">
                Everything you need to know about <strong>commercial pool cleaning robots in India</strong>
              </p>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button 
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-all"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Explore More Harpalyk Solutions</h3>
            <div className="flex flex-wrap gap-4">
              <a href="/#products" className="text-cyan-600 hover:text-cyan-700 underline">Floor Cleaning Robots</a>
              <a href="/#products" className="text-cyan-600 hover:text-cyan-700 underline">Robot Vacuum Cleaners</a>
              <a href="/#contact" className="text-cyan-600 hover:text-cyan-700 underline">Book a Demo</a>
              <a href="/#about" className="text-cyan-600 hover:text-cyan-700 underline">About Harpalyk</a>
              <a href="/#faq" className="text-cyan-600 hover:text-cyan-700 underline">Support & FAQs</a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default CommercialPoolRobotPage;
